import React, { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { SITE } from "@/lib/site";
import { useContent } from "@/lib/content";

export default function VideoTour() {
  const { content } = useContent();
  const v = content.video_tour || {};
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  const videoUrl = v.video_url || SITE.media.tourVideo;
  const posterUrl = v.poster_url || SITE.media.classroomA;
  const bullets = v.bullets || [
    "Faculty-led, mentor-supported study tracks",
    "Weekly tests, mock exams and revision marathons",
    "Parent updates so families stay involved",
  ];

  return (
    <section id="tour" data-testid="video-tour-section" className="bg-[#f8fafc] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">
              {v.eyebrow || "Step Inside Our Classroom"}
            </p>
            <h2 className="mt-3 font-rubik text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] leading-tight text-balance">
              {v.headline_pre || "See our"} <span className="text-[#002570]">{v.headline_emphasis || "U-Shaped Interactive Classes"}</span> {v.headline_post || "in action."}
            </h2>
            <p className="mt-5 font-inter text-base md:text-lg text-slate-600 leading-relaxed">
              {v.body || "Small batches, eye contact with the faculty, instant doubt-clearing and a feedback loop that prevents anyone from being left behind. That's how we engineer pass rates."}
            </p>
            <ul className="mt-6 space-y-3 font-inter text-slate-700">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3"><span className="mt-1.5 w-2 h-2 rounded-full bg-[#73c3ff]" /> {b}</li>
              ))}
            </ul>
          </div>
          <div className="relative group rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white" data-testid="video-tour-poster">
            <video
              ref={videoRef}
              src={videoUrl}
              poster={posterUrl}
              playsInline
              preload="metadata"
              onEnded={() => setPlaying(false)}
              onPause={() => setPlaying(false)}
              onPlay={() => setPlaying(true)}
              className="w-full h-[420px] md:h-[520px] object-cover bg-black"
            />
            {!playing && <div className="absolute inset-0 bg-gradient-to-t from-[#002570]/70 via-[#002570]/10 to-transparent pointer-events-none" />}
            <button onClick={toggle} data-testid="play-tour-btn" aria-label={playing ? "Pause tour" : "Play tour"} className="absolute inset-0 flex items-center justify-center">
              <span className={`relative w-20 h-20 rounded-full bg-white text-[#002570] flex items-center justify-center shadow-2xl transition-all ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100 group-hover:scale-110"}`}>
                {playing ? <Pause className="w-8 h-8 fill-[#002570]" /> : <Play className="w-8 h-8 fill-[#002570] ml-1" />}
              </span>
            </button>
            {!playing && (
              <div className="absolute bottom-5 left-5 right-5 text-white pointer-events-none">
                <p className="font-rubik text-lg font-semibold">{v.title || "Why Students Choose Educators"}</p>
                <p className="font-inter text-sm text-white/80">{v.subtitle || "Tap to play campus walkthrough"}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
